import '../Styles/App.css';
import { Element } from 'react-scroll';

import ReusableAppBar from '../Components/UI/ReusableAppBar';
import Footer from '../Components/Layout/Footer';
import ProjectTemplate from '../Components/Projects/ProjectTemplate';

import CoverImage from '../Assets/Images/WhileTheySleepCover.png';
import gallery1 from '../Assets/Images/WhileTheySleep (1).png';
import gallery2 from '../Assets/Images/WhileTheySleep (2).png';
import gallery3 from '../Assets/Images/WhileTheySleep (3).png';
import gallery4 from '../Assets/Images/WhileTheySleep (4).png';
import gallery5 from '../Assets/Images/WhileTheySleep (5).png';
import gallery6 from '../Assets/Images/WhileTheySleep (5).png';
import HeroGif from '../Assets/Gifs/WhileTheySleep.gif';

function WhileTheySleep() {
  return (
    <main>
      <ReusableAppBar />

      <ProjectTemplate
        CoverImageSRC={CoverImage}
        HeroGif={HeroGif}
        ProjectTitle="While They Sleep"
        ProjectSummary="A solo-developed PSX-style stealth horror game built in Unity. The project focuses on player movement, stealth detection, noise systems, interaction logic, task progression, UI feedback, jumpscare sequencing, and overall gameplay system integration."
        ProjectLink="https://rileyclarke.itch.io/while-they-sleep"
        ProjectDemo="https://www.youtube.com/embed/1Pv7n2BCKhQ"

        ProjectType="Solo Indie / Game Jam Style Project"
        ProjectEngine="Unity"
        ProjectLanguage="C#"
        ProjectTeamSize="Solo Developer"
        ProjectRole="Gameplay Programmer / Systems Designer / Solo Developer"
        ProjectTimeFrame="Game jam version: 72 hours / Continuing development"
        BuildStatus="Playable vertical slice / active portfolio project"

        TechStack={[
          'Unity',
          'C#',
          'Github',
          'New Input System',
          'UI Systems',
          'Stealth Systems',
          'Task System',
          'Detection Logic',
          'Audio Feedback',
          'Scene Transitions',
        ]}

        Overview="While They Sleep is a PSX-inspired stealth horror game where the player controls a goblin living in the basement of a house and sneaks upstairs at night to complete tasks, steal supplies, and avoid detection. The project was built as a solo game and reflects my interest in creating tense atmosphere through interacting gameplay systems rather than relying only on visuals."

        Contributions={[
          'Designed and programmed the core first-person player controller, including movement, crouching, footsteps, and camera behaviour.',
          'Built a noise meter system that increases from player movement and object interaction, delays before decay, and affects tension and failure states.',
          'Implemented interactable object systems for picking up, throwing, rotating, and using world items during stealth gameplay.',
          'Created stealth detection systems including security cameras, spotlight-based vision, detection meters, alarm logic, and player feedback.',
          'Built task-based progression systems to support a more structured game loop with on-screen objectives and completion tracking.',
          'Programmed the lose condition flow with jumpscare triggering, movement freeze, audio interruption, camera shake, and screen transitions.',
          'Integrated UI, ambient audio, post-processing atmosphere, and extraction flow into a cohesive playable experience as a solo developer.',
        ]}


        ProgrammingConcepts={[
          {
            title: 'Object-Oriented System Design',
            description:
              'The project is broken into focused gameplay systems such as player control, noise tracking, task management, interactables, jumpscare management, and screen fading. This helped keep responsibilities separated and made systems easier to expand.',
          },
          {
            title: 'State and Event-Driven Gameplay Logic',
            description:
              'Detection, alarm behaviour, lose conditions, extraction triggers, and task completion all depend on events and changing gameplay states. This let the project respond cleanly to player actions and tension spikes.',
          },
          {
            title: 'UI Programming and Feedback Loops',
            description:
              'The game relies on UI for communicating objective progress, noise buildup, interaction prompts, and transitions. I used UI not just as decoration, but as a core part of player readability and tension management.',
          },
          {
            title: 'Player Interaction Systems',
            description:
              'I implemented systems for picking up, rotating, dropping, and throwing objects, which helped make the house feel interactive and supported both stealth and task-based gameplay.',
          },
          {
            title: 'Stealth Detection Logic',
            description:
              'Security cameras, view cones, detection meters, and alarm escalation were used to create readable stealth pressure. These systems needed to be clear to the player while still feeling threatening.',
          },
          {
            title: 'System Integration and Game Flow',
            description:
              'One of the strongest parts of this project is how different systems connect: movement, sound, detection, UI, jumpscare flow, and scene transitions all feed into the same player experience.',
          },
          {
            title: 'Modular Task System',
            description:
              'One of the strongest parts of this project is how different systems connect: movement, sound, detection, UI, jumpscare flow, and scene transitions all feed into the same player experience.',
          },
        ]}

        Challenges={[
          {
            challenge:
              'The game needed tension to come from gameplay systems rather than only from visuals or scripted horror moments.',
            solution:
              'I built a noise system, stealth detection, alarm escalation, and a lose-state sequence so the player’s own actions could create rising tension and risk during play.',
          },
          {
            challenge:
              'Because there are many interconnected systems, the project could easily become hard to manage if every mechanic directly referenced every other script.',
            solution:
              'I separated responsibilities into managers and focused scripts such as the noise meter, jumpscare manager, task UI controller, and screen fader so each system could handle a clear role.',
          },
          {
            challenge:
              'The game loop originally leaned more toward hoarding and score, but I wanted a more directed structure that better fit the setting and pacing.',
            solution:
              'I shifted the design toward a modular task-based system where the player sneaks upstairs to retrieve food, water, and other necessities, making progression more linear, readable, and purposeful.',
          },
        ]}

        CompletionLevel="This project functions as a playable vertical slice with multiple integrated mechanics, a clear atmosphere, and a defined gameplay loop. It goes beyond a simple prototype because the systems support one another and the player can experience a full intended stealth-horror sequence."

        PolishSummary="The project includes integrated UI, audio feedback, stealth systems, interaction mechanics, failure states, and transitions. It also has a strong visual identity and cohesive tone, which helps it feel like a complete experience rather than a collection of disconnected mechanics."

        IntegratedSystems={[
          'First-person player controller',
          'Crouch and movement states',
          'Head bob and camera behaviour',
          'Footstep and terrain audio',
          'Noise meter with delayed decay',
          'Object pickup / throw / rotate interactions',
          'Security camera detection',
          'Alarm and tension escalation',
          'Task-based progression',
          'Interaction prompts',
          'Jumpscare lose condition',
          'Scene fade and transition management',
        ]}

        KnownLimitations="As an actively developed solo project, some systems are still being refined and expanded. The emphasis so far has been on building a strong gameplay foundation and integrating mechanics cleanly before pushing further into content scale."

        GameplayNote="This is the video of me presenting the completed build for the Jam at RRC. Note that the audio was not picking up very well on stream."

        CodeSamples={[
          {
            title: 'Noise Meter UI',
            code: `
    private void UpdateNoiseDecay()
    {
        timeSinceLastNoise += Time.deltaTime;

        //Only begin reducing the target noise after the configured delay
        if (timeSinceLastNoise >= decayDelay)
        {
            targetNoise = Mathf.Max(0f, targetNoise - decaySpeed * Time.deltaTime);
        }
    }

    private void UpdateDisplayedNoise()
    {
        if (displayedNoise < targetNoise)
        {
            //Fast, responsive increase
            displayedNoise = Mathf.Lerp(displayedNoise, targetNoise, riseSpeed * Time.deltaTime);
        }
        else
        {
            //More controlled drop
            displayedNoise = Mathf.MoveTowards(displayedNoise, targetNoise, decaySpeed * Time.deltaTime);
        }
    }`,
            notes: [
              'Shows how the NoiseMeter UI is updated in game.',
              'Represents how tension is created through accumulation and delayed decay.',
              'Works in update loop',
              'Target noise gets added to from every script where the player creates noise, such as walking, opening doors, or hitting objects against things',
            ],
          },
          {
            title: 'Task Reporting',
            code: `
    #region Task Reporting
    public void ReportTaskEvent(TaskType eventType, string reportedTargetID, int amount = 1)
    {
        foreach (TaskData task in currentTasks)
        {
            //Cycle through taskstate, tasktype, and the target id to ensure proper the proper task gets affected
            if (task.taskState != TaskState.Active)
                continue;

            if (task.taskType != eventType)
                continue;

            if (task.targetID != reportedTargetID)
                continue;

            //Add the task amount to the proper task
            task.currentAmount += amount;

            //Checks for completion
            if (task.currentAmount >= task.requiredAmount)
            {
                CompleteTask(task.taskID);
            }
        }

        RefreshTaskUI();
    }
    #endregion`,
            notes: [
              'Shows how tasks get reported in game.',
              'Called from a task reporter script on each task object (ex. Enter zone script calls taskreporter when entering zone trigger).',
              'Takes in the event type of task (zone, interact, pickup), the specific reported taskID, and the amount to add to the task.',
              'Cycles through current task list to ensure proper tasks are progressed.',
            ],
          },
          {
            title: 'TaskData Scriptable Object',
            code: `public class TaskData : ScriptableObject
{
    [Header("Basic Info")]
    public string taskID; //Internal Name
    public string taskName; //Display Name
    [TextArea] public string description;
    public AudioClip taskCompletionAudioClip;

    [Header("Task Settings")]
    public TaskType taskType;
    public string targetID; //object it listens for
    public bool isRequired = true; 
    public bool startHidden = false; //For multi-stage tasks

    [Header("Progress")]
    public int requiredAmount = 1; //ex. "Grab food: x/requiredAmount"
    public int currentAmount = 0;

    [Header("Dependencies")]
    public List<string> prerequisiteTaskIDs = new List<string>(); //Tasks unlock after others are done

    [Header("Runtime State")]
    public TaskState taskState = TaskState.Locked;
}

public enum TaskType
{
    Interact,
    Pickup,
    EnterZone
}

public enum TaskState
{
    Locked,
    Active,
    Completed,
    Failed
}`,
            notes: [
              'Shows the data stored in each TaskData object.',
              'System allows for iteration on tasks without the need for creating additional code.',
              'Data used by every task-related script in the game.',
            ],
          },
                    {
            title: 'Typing Monologue Effect',
            code: 
            `
    IEnumerator TypeMonologue(string message)
    {
        //Initialize text
        monologueText.text = "";
        charCounter = 0;

        //Wait for night text to be fully faded before displaying
        if(nightUI.isTextFaded == false)
        {
            yield return new WaitForSeconds(nightUI.textDuration + nightUI.fadeDuration);
        }

        //Additional delay
        yield return new WaitForSeconds(1.25f);

        //Set visible
        if (monologueCanvasGroup != null)
        {
            monologueCanvasGroup.alpha = 1f;
        }

        //Typing effect
        for (int i = 0; i < message.Length; i++)
        {
            char currentChar = message[i];

            monologueText.text += currentChar;

            //Only play sound on non-space characters
            if (!char.IsWhiteSpace(currentChar))
            {
                charCounter++;

                if (charCounter >= soundInterval)
                {
                    PlayTypeSound();
                    charCounter = 0;
                }
            }

            yield return new WaitForSeconds(typeSpeed);
        }

        //Wait after typing
        yield return new WaitForSeconds(monologueDuration);

        //Fade out over time
        if (monologueCanvasGroup != null)
        {
            float startAlpha = monologueCanvasGroup.alpha;
            float time = 0f;

            while (time < fadeDuration)
            {
                time += Time.deltaTime;

                float t = time / fadeDuration;
                monologueCanvasGroup.alpha = Mathf.Lerp(startAlpha, 0f, t);

                yield return null;
            }

            //Ensure it dissapears if fading fails
            monologueCanvasGroup.alpha = 0f;
        }

        typingCoroutine = null;
    }`,
            notes: [
              'Shows the way the typing effect works.',
              'Re-usable for other dialogue-related events. I may adjust this to reside in another more modular script.',
              'Data used by every task-related script in the game.',
            ],
          },
        ]}

        GalleryImages={[gallery1, gallery2, gallery3, gallery4, gallery5, gallery6]}

        Reflection="While They Sleep is one of the strongest examples of my current programming ability because it combines multiple interdependent gameplay systems into a focused player experience. It reflects confidence in player control, interactions, UI scripting, gameplay feedback, and moment-to-moment system design. It also shows my growth in structuring Unity projects more cleanly, especially when building managers and reusable systems that support a larger gameplay loop. This was my first project using github for version control, and I have learned the value in being able to iterate with it and look back on my progress from the game jam version of the game to now."
      />

      <Element name="Contact">
        <div>
          <Footer />
        </div>
      </Element>
    </main>
  );
}

export default WhileTheySleep;